import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessagesSquare, ArrowRight } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { useDataLabel } from '@/lib/dataI18n';

/**
 * Lists `userId`'s submitted assignments. Each row deep-links to
 * /admin/questionnaires/{qid}?user={uid} where the ResponseViewer is
 * mounted with a per-user filter.
 */
export default function UserResponsesDialog({ open, onOpenChange, user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tData = useDataLabel();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    let alive = true;
    setLoading(true);
    adminApi.listAssignments()
      .then((rows) => {
        if (!alive) return;
        const mine = (rows || []).filter((r) => r.assigned_to === user.id);
        setItems(mine.filter((r) => r.status === 'submitted' || r.submitted_at));
      })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, user?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="user-responses-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessagesSquare className="h-5 w-5 text-primary" />
            {t('pages.users.responsesTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('pages.users.responsesSubtitle', { name: user?.name || user?.email || '' })}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-6 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
          </div>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {t('pages.users.noSubmissions')}
          </p>
        ) : (
          <ul className="divide-y divide-border max-h-[60vh] overflow-auto">
            {items.map((a) => (
              <li key={a.id} data-testid={`user-resp-row-${a.id}`} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{tData(a.title || '')}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <Badge variant="outline" className="text-xs">{a.suite_code}</Badge>
                    <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/20">
                      {t(`common.submitted`, { defaultValue: 'Submitted' })}
                    </Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  data-testid={`user-resp-open-${a.id}`}
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/admin/questionnaires/${a.questionnaire_id}?user=${user.id}`);
                  }}
                  className="gap-1.5"
                >
                  {t('pages.users.openResponses')} <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
